import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { notify } from "@/utils";

import type { GroupDetails, GroupParticipant } from "../../services/group";
import {
  useFetchGroupParticipants,
  useSyncGroupParticipants,
} from "../../services/group";
import GroupAddUsersPage from "./GroupAddUsersPage";
import GroupInviteLinkModal from "./GroupInviteLinkModal";
import GroupMemberActions from "./GroupMemberActions";
import GroupMemberRow from "./GroupMemberRow";
import GroupSubPageHeader from "./GroupSubPageHeader";
import { GroupUserRowSkeleton } from "./GroupUserListSkeleton";

type GroupMembersEditPageProps = {
  groupId: number;
  groupDetails?: GroupDetails;
  inviteUrl: string;
  isLoading?: boolean;
  onBack: () => void;
  onCopyLink: () => void;
};

export default function GroupMembersEditPage({
  groupId,
  groupDetails,
  inviteUrl,
  isLoading,
  onBack,
  onCopyLink,
}: GroupMembersEditPageProps) {
  const { t } = useTranslation("messenger.user");
  const [showAddUsers, setShowAddUsers] = useState(false);
  const [showInviteLink, setShowInviteLink] = useState(false);

  const { data: participants } = useFetchGroupParticipants(groupId);
  const syncParticipants = useSyncGroupParticipants(groupId);

  const participantIds = useMemo(
    () => new Set(participants?.data?.map((p) => p.id) ?? []),
    [participants?.data],
  );

  const members = useMemo(() => {
    const ownerId = groupDetails?.owner?.id;
    const data = (participants?.data ?? []).map((p) => ({
      ...p,
      is_owner: p.is_owner || p.id === ownerId,
    }));
    const hasOwner = !ownerId || data.some((p) => p.id === ownerId);
    const list: GroupParticipant[] = hasOwner
      ? data
      : [
          { ...groupDetails!.owner, is_owner: true } as GroupParticipant,
          ...data,
        ];
    return [...list].sort((a, b) => Number(b.is_owner) - Number(a.is_owner));
  }, [groupDetails, participants?.data]);

  const handleAddUsers = (newUserIds: number[]) => {
    const existingUserIds = participants?.data?.map((p) => p.id) ?? [];
    syncParticipants.mutate(
      { user_ids: [...existingUserIds, ...newUserIds] },
      {
        onSuccess: () => {
          notify.success(t("Users added successfully"));
          setShowAddUsers(false);
        },
        onError: () => notify.error(t("Failed to add users")),
      },
    );
  };

  if (showAddUsers) {
    return (
      <GroupAddUsersPage
        participantIds={participantIds}
        onBack={() => setShowAddUsers(false)}
        onConfirm={handleAddUsers}
        isSubmitting={syncParticipants.isPending}
      />
    );
  }

  return (
    <div className="flex h-full flex-col bg-white">
      <GroupSubPageHeader
        title={t("Members")}
        onBack={onBack}
        onConfirm={onBack}
      />

      {groupDetails?.can_update === true && (
        <GroupMemberActions
          onAddUsers={() => setShowAddUsers(true)}
          onInviteLink={() => setShowInviteLink(true)}
        />
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading
          ? [...Array(4)].map((_, index) => (
              <GroupUserRowSkeleton key={index} />
            ))
          : members.map((participant) => (
              <GroupMemberRow key={participant.id} participant={participant} />
            ))}
      </div>

      <GroupInviteLinkModal
        open={showInviteLink}
        onClose={() => setShowInviteLink(false)}
        inviteUrl={inviteUrl}
        onCopy={onCopyLink}
      />
    </div>
  );
}
